import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X, Eye, EyeOff } from 'react-feather';
import { brandTaxComplianceService } from '../../../services/brand-tax-compliance.service';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface TaxInfoViewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  affiliateId: string;
  taxYear: number;
}

export function TaxInfoViewDrawer({
  isOpen,
  onClose,
  affiliateId,
  taxYear,
}: TaxInfoViewDrawerProps) {
  const [taxInfo, setTaxInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [revealedTaxId, setRevealedTaxId] = useState<string | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTaxInfo();
    } else {
      // Reset state when drawer closes
      setTaxInfo(null);
      setRevealedTaxId(null);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        setHideTimeout(null);
      }
    }
  }, [isOpen, affiliateId]);

  const fetchTaxInfo = async () => {
    try {
      setIsLoading(true);
      const data = await brandTaxComplianceService.getAffiliateTaxInfo(affiliateId, taxYear);
      setTaxInfo(data);
    } catch (error: any) {
      console.error('Error fetching tax info:', error);
      toast.error(error?.response?.data?.message || 'Failed to load tax information');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevealTaxId = async () => {
    if (!taxInfo) return;

    try {
      setIsRevealing(true);
      const response = await brandTaxComplianceService.revealTaxId(
        affiliateId,
        taxInfo.id
      );
      setRevealedTaxId(response.taxId);

      // Auto-hide after 5 seconds
      const timeout = setTimeout(() => {
        setRevealedTaxId(null);
        setHideTimeout(null);
      }, 5000);
      setHideTimeout(timeout);
    } catch (error: any) {
      console.error('Error revealing tax ID:', error);
      toast.error(error?.response?.data?.message || 'Failed to reveal tax ID');
    } finally {
      setIsRevealing(false);
    }
  };

  const formatAddress = (address: any): string => {
    if (!address) return 'N/A';
    
    if (typeof address === 'string') return address;
    
    if (address.permanent) {
      // W-8BEN format
      const perm = address.permanent;
      const parts = [
        perm.street,
        perm.city,
        perm.state,
        perm.zipCode,
        perm.country,
      ].filter(Boolean);
      return parts.join(', ');
    }

    // Standard format
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zipCode,
      address.country,
    ].filter(Boolean);

    return parts.join(', ');
  };

  const formatTaxId = (last4: string, revealed?: string | null): string => {
    if (revealed) {
      // Format revealed tax ID
      const cleaned = revealed.replace(/\D/g, '');
      if (cleaned.length === 9) {
        // SSN or EIN format
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
      }
      return revealed;
    }
    return last4 ? `XXX-XX-${last4}` : 'N/A';
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-card shadow-xl">
                    {/* Header */}
                    <div className="bg-muted px-6 py-4 border-b border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <Dialog.Title className="text-lg font-semibold text-foreground">
                            Affiliate Tax Information
                          </Dialog.Title>
                          <p className="mt-1 text-sm text-muted-foreground">
                            For tax year {taxYear}
                          </p>
                        </div>
                        <button
                          onClick={onClose}
                          className="text-muted-foreground hover:text-muted-foreground"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 px-6 py-6">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="text-muted-foreground">Loading...</div>
                        </div>
                      ) : taxInfo ? (
                        <div className="space-y-8">
                          {/* Section A: Affiliate Identity */}
                          <div>
                            <h3 className="text-sm font-medium text-foreground mb-4">
                              Affiliate Identity
                            </h3>
                            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Name</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {taxInfo.legalName}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Entity Type</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {taxInfo.entityType || 'Individual'}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Country</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {taxInfo.taxCountry}
                                </dd>
                              </div>
                              {taxInfo.businessName && (
                                <div>
                                  <dt className="text-sm font-medium text-muted-foreground">Business Name</dt>
                                  <dd className="mt-1 text-sm text-foreground">
                                    {taxInfo.businessName}
                                  </dd>
                                </div>
                              )}
                            </dl>
                          </div>

                          {/* Section B: Address */}
                          <div>
                            <h3 className="text-sm font-medium text-foreground mb-4">Address</h3>
                            <dl>
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Address</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {formatAddress(taxInfo.address)}
                                </dd>
                              </div>
                            </dl>
                          </div>

                          {/* Section C: Tax Identification */}
                          <div>
                            <h3 className="text-sm font-medium text-foreground mb-4">
                              Tax Identification (Masked)
                            </h3>
                            <dl>
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">
                                  Tax ID ({taxInfo.formType === 'W9' ? 'SSN/EIN' : 'Foreign Tax ID'})
                                </dt>
                                <dd className="mt-1 flex items-center gap-3">
                                  <span className="text-sm text-foreground font-mono">
                                    {formatTaxId(taxInfo.taxIdLast4, revealedTaxId)}
                                  </span>
                                  {!revealedTaxId && (
                                    <button
                                      onClick={handleRevealTaxId}
                                      disabled={isRevealing}
                                      className="text-xs px-3 py-1.5 bg-brand-100 text-brand-700 rounded-md hover:bg-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-500 flex items-center gap-1 disabled:opacity-50"
                                      onMouseDown={(e) => e.preventDefault()}
                                    >
                                      {isRevealing ? (
                                        <>
                                          <EyeOff className="w-3 h-3" />
                                          Revealing...
                                        </>
                                      ) : (
                                        <>
                                          <Eye className="w-3 h-3" />
                                          Hold to reveal
                                        </>
                                      )}
                                    </button>
                                  )}
                                  {revealedTaxId && (
                                    <span className="text-xs text-muted-foreground">(Auto-hides in 5s)</span>
                                  )}
                                </dd>
                                <p className="mt-2 text-xs text-muted-foreground">
                                  Your information is encrypted and never shared via email.
                                </p>
                              </div>
                            </dl>
                          </div>

                          {/* Section D: Certification Metadata */}
                          <div>
                            <h3 className="text-sm font-medium text-foreground mb-4">
                              Certification Metadata
                            </h3>
                            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Form Type</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {taxInfo.formType}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Certified On</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {taxInfo.addresses?.[0]?.certifiedAt
                                    ? format(new Date(taxInfo.addresses[0].certifiedAt), 'MMM d, yyyy')
                                    : 'N/A'}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-sm font-medium text-muted-foreground">Tax Year</dt>
                                <dd className="mt-1 text-sm text-foreground">
                                  {taxInfo.taxYear}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <p className="text-muted-foreground">No tax information found</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

